import React from "react";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

const cellText = (value) => (
  <MDTypography
    component="p"
    variant="button"
    color="text"
    fontWeight="regular"
  >
    {value}
  </MDTypography>
);

const cellCaption = (value) => (
  <MDTypography
    component="p"
    variant="caption"
    color="text"
    fontWeight="regular"
  >
    {value}
  </MDTypography>
);

const cellBlock = (value) => (
  <MDTypography
    component="div"
    variant="caption"
    color="text"
    fontWeight="regular"
  >
    {value}
  </MDTypography>
);

const cellRequired = (value) => (
  <MDTypography
    component="p"
    variant="button"
    color="text"
    fontWeight="regular"
  >
    {value}
    <span style={{ color: "#ff4848" }}>*</span>
  </MDTypography>
);

const badge = (type) => (
  <MDTypography
    component="p"
    variant="caption"
    color="text"
    fontWeight="regular"
  >
    <MDBadge
      color="success"
      badgeContent={type}
      variant="gradient"
      size="sm"
      sx={{
        "& .MuiBadge-badge": {
          background: "#022DEC",
          backgroundImage: "none",
        },
      }}
    ></MDBadge>
  </MDTypography>
);

const requestBodyColumnHeaders = [
  { Header: "S/N", accessor: "num", align: "left", width: "5%" },
  { Header: "Parameter", accessor: "fieldName", align: "left", width: "18%" },
  { Header: "Data Type", accessor: "dataType", align: "center", width: "10%" },
  { Header: "Parameter Type", accessor: "paramType", align: "left", width: "12%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const queryParamColumnHeaders = [
  { Header: "S/N", accessor: "num", align: "left", width: "5%" },
  { Header: "Parameter", accessor: "fieldName", align: "left", width: "18%" },
  { Header: "Data Type", accessor: "dataType", align: "center", width: "10%" },
  { Header: "Parameter Type", accessor: "paramType", align: "left", width: "12%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const idMappingColumnHeaders = [
  { Header: "ID", accessor: "num", align: "left", width: "10%" },
  { Header: "Column Name", accessor: "fieldName", align: "left", width: "35%" },
  { Header: "Search Format", accessor: "description", align: "left" },
];

const eventTypeColumnHeaders = [
  { Header: "ID", accessor: "num", align: "left", width: "10%" },
  { Header: "Event Name", accessor: "fieldName", align: "left" },
];

const responseMessageColumnHeaders = [
  {
    Header: "HTTP Status Code",
    accessor: "num",
    align: "left",
    width: "18%",
  },
  { Header: "Details", accessor: "fieldName", align: "left", width: "32%" },
  { Header: "Response Model", accessor: "description", align: "left" },
];

const columnFilterRows = [
  ["0", "No filteration", "None"],
  ["1", "Expiration Date", "YYYY-MM-DD"],
  ["2", "Anticipated Payment Date", "YYYY-MM-DD"],
  ["3", "Protect Expiration Date", "YYYY-MM-DD"],
  ["4", "Effective Date", "YYYY-MM-DD"],
  ["5", "CUSIP", "Cusip value"],
  ["6", "Gross Rate", "Gross Rate value"],
  ["7", "Ex Date", "YYYY-MM-DD"],
  [
    "8",
    "Mandatory / Voluntary",
    'Search "MAND" for mandatory, "CHOS" for choice and "VOLU" for voluntary',
  ],
  ["9", "Payout Rate", "Payout Rate value"],
  ["10", "Position Capture Date", "YYYY-MM-DD"],
  ["11", "Record Date", "YYYY-MM-DD"],
  ["12", "Tax Rate", "Tax Rate value"],
  ["13", "Ticker Symbol", "Ticker Symbol value"],
  ["14", "Pay Date", "YYYY-MM-DD"],
].map(([id, name, fmt]) => ({
  num: cellText(id),
  fieldName: cellText(name),
  description: cellCaption(fmt),
}));

const eventTypeRows = [
  [1, "Dividend Reinvestment"],
  [2, "Capital Gains Distribution"],
  [3, "Cash Dividend"],
  [4, "Change in Domicile"],
  [5, "Consent"],
  [6, "Conversion"],
  [7, "Bond Default"],
  [8, "Dividend Option"],
  [9, "Dutch Auction"],
  [10, "Exchange"],
  [11, "Full Call"],
  [12, "Prefunding"],
  [13, "General Information"],
  [14, "Interest Payment"],
  [15, "Liquidation"],
  [16, "Put Redemption"],
  [17, "Redemption"],
  [18, "Annual General Meeting (Proxy Message)"],
  [19, "Extraordinary Meeting (Proxy Message)"],
  [20, "Special Meeting (Proxy Message)"],
  [21, "Merger"],
  [22, "Change"],
  [23, "Odd Lot Sale Purchase"],
  [24, "Partial Call"],
  [25, "Payment in Kind"],
  [26, "Bankruptcy"],
  [27, "Capitalisation"],
  [28, "Partial Redemption Without Nominal Value Reduction"],
  [29, "Other Event"],
  [30, "Capital Distribution"],
  [31, "Reverse Stock Split"],
  [32, "Rights Distribution"],
  [33, "Rights Subscription"],
  [34, "Detachment"],
  [35, "Spin Off"],
  [36, "Stock Dividend"],
  [37, "Stock Split"],
  [38, "Tax Event"],
  [39, "Tax Reclaim"],
  [40, "Tender"],
  [41, "Repurchase Offer"],
  [42, "Warrant Exercise"],
  [43, "Worthless"],
].map(([id, name]) => ({
  num: cellText(String(id)),
  fieldName: cellText(name),
}));

const codeBlock = (code) => (
  <pre
    style={{
      background: "transparent",
      fontSize: "12px",
      lineHeight: 1.5,
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {code}
  </pre>
);

const statusCodePill = (text, className) => (
  <p className={`staus-code ${className}`} style={{ margin: 0, display: "inline-block" }}>
    {text}
  </p>
);

const responseMessageRows = [
  {
    num: statusCodePill("Status Code: 200 (Success)", "code-success"),
    fieldName: cellCaption("Data fetched successfully."),
    description: cellBlock(
      codeBlock(`{
  "errors": [],
  "isSuccess": true,
  "message": "Data fetched successfully.",
  "data": [ ... ],
  "statusCode": 200
}`)
    ),
  },
  {
    num: statusCodePill("Status Code: 204 (No Content)", "no-content"),
    fieldName: cellCaption("No data found."),
    description: cellBlock(
      codeBlock(`{
  "errors": [],
  "isSuccess": true,
  "message": "No data found.",
  "data": [],
  "statusCode": 204
}`)
    ),
  },
  {
    num: statusCodePill("Status Code: 401 (Unauthorized)", "code-red"),
    fieldName: cellCaption("User access token is invalid or expired."),
    description: cellCaption("Error: Unauthorized"),
  },
  {
    num: statusCodePill("Status Code: 400 (Bad Request)", "code-red"),
    fieldName: cellCaption(
      "The filterType or EventTypes are out of range or in an incorrect format."
    ),
    description: cellBlock(
      codeBlock(`"errors": {
  "EventTypes": [
    "EventTypes are not within the specified range."
  ],
  "FilterType": [
    "The value '44' is invalid."
  ]
}`)
    ),
  },
];

const responseSplits = [
  {
    status: "Status Code: 200 (Success)",
    statusClass: "code-success",
    details: "Data fetched successfully.",
    code: `{
  "errors": [],
  "isSuccess": true,
  "message": "Data fetched successfully.",
  "data": [ ... ],
  "statusCode": 200
}`,
  },
  {
    status: "Status Code: 204 (No Content)",
    statusClass: "accepted",
    details: "No data found.",
    code: `{
  "errors": [],
  "isSuccess": true,
  "message": "No data found.",
  "data": [],
  "statusCode": 204
}`,
  },
  {
    status: "Status Code: 401 (Unauthorized)",
    statusClass: "code-red",
    details: "User access token is invalid or expired.",
    code: `Error: Unauthorized`,
  },
  {
    status: "Status Code: 400 (Bad Request)",
    statusClass: "code-red",
    details:
      "The filterType or EventTypes are out of range or in an incorrect format.",
    code: `"errors": {
  "EventTypes": [
    "EventTypes are not within the specified range."
  ],
  "FilterType": [
    "The value '44' is invalid."
  ]
}`,
  },
];

const requestHeaderTableData = {
  columns: [],
  rows: [
    {
      num: cellText("1"),
      key: cellText("Authorization"),
      value: cellCaption("Bearer <access_token>"),
    },
  ],
};

const vfluxApiData = [
  {
    mainHeadingStatic: "Overview",
    mainGridIdStatic: "introduction",
    mainHeadingDescStatic:
      "The purpose of this API is to fetch the corporate action details as per the user-provided filters.",
  },
  {
    mainHeadingStatic: "Getting Started",
    mainGridIdStatic: "getting-started",
    descWithLink: {
      desc: "The current version of this API is live at",
      link: "https://api.gflux.io/index.html",
    },
    staticTables: [
      {
        half: true,
        columns: [
          { Header: "Endpoint", accessor: "fieldName", align: "left", width: "35%" },
          { Header: "What it does", accessor: "description", align: "left" },
        ],
        rows: [
          {
            fieldName: cellText("/api/External/GetEvents"),
            description: cellCaption(
              "Returns an array of corporate action events based on query parameters."
            ),
          },
        ],
      },
    ],
  },
  {
    mainHeadingStatic: "API Calls",
    mainGridIdStatic: "api-calls",
    mainHeadingDescStatic:
      "To access the API, you must first get the JWT token from the login endpoint.",
  },
  {
    mainHeading: "Login API",
    mainGridId: "login-api",
    mainHeadingDesc:
      "Returns an object that contains user access token and refresh token.",
    endpointId: "login-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "{{base-url}}/login",
    requestBody: "Request Parameters",
    requestBodyId: "login-request-body",
    requestBodyTableId: "login-request-body-table",
    requestBodyTable: {
      columns: [...requestBodyColumnHeaders],
      rows: [
        {
          num: cellText("1"),
          fieldName: cellRequired("userName"),
          description: cellCaption("Name of the User."),
          paramType: cellCaption("body"),
          dataType: badge("String"),
        },
        {
          num: cellText("2"),
          fieldName: cellRequired("password"),
          description: cellCaption("Password of the User."),
          paramType: cellCaption("body"),
          dataType: badge("String"),
        },
        {
          num: cellText("3"),
          fieldName: cellText("token"),
          description: cellCaption(
            "Unnecessary field, just pass empty string or leave it as it is."
          ),
          paramType: cellCaption("body"),
          dataType: badge("String"),
        },
      ],
    },
    requestBodyNote:
      "The \"token\" will be used for accessing the \"Corporate Action events\" API.",
    reqObjKey: "vflux_login_request",
    respObjKey: "vflux_login_response",
    responseId: "login-response",
  },
  {
    mainHeading: "Corporate Action Events API",
    mainGridId: "get-events-api",
    mainHeadingDesc:
      "Authorize the endpoint with the access token retrieved by the Login API. Returns an array of corporate action events based on query parameters. ",
    endpointId: "get-events-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc:
      "/api/External/GetEvents",
    requestBody: "Request Parameters",
    requestBodyId: "get-events-query-params",
    requestBodyTable: {
      columns: [...queryParamColumnHeaders],
      rows: [
        {
          num: cellText("1"),
          fieldName: cellText("FilterValue"),
          description: (
            <MDTypography
              component="p"
              variant="caption"
              color="text"
              fontWeight="regular"
            >
              The value to be searched.
              <br />
              Note:
              <br />
              No filter will be applied if the field is empty.
            </MDTypography>
          ),
          paramType: cellCaption("query"),
          dataType: badge("String"),
        },
        {
          num: cellText("2"),
          fieldName: cellText("FilterType"),
          description: (
            <MDTypography
              component="p"
              variant="caption"
              color="text"
              fontWeight="regular"
            >
              The &apos;ID&apos; of column on which the search should be applied.
              <br />
              Note:
              <br />
              1. Please find the column filter type ID from the &apos;Column Filter Type and ID Mapping&apos; listed below.
              <br />
              2. No filter will be applied if the field is empty.
            </MDTypography>
          ),
          paramType: cellCaption("query"),
          dataType: badge("String"),
        },
        {
          num: cellText("3"),
          fieldName: cellText("EventTypes"),
          description: (
            <MDTypography
              component="p"
              variant="caption"
              color="text"
              fontWeight="regular"
            >
              The list of comma separated &apos;ID&apos; of events on which the filters will be applied.
              <br />
              Note:
              <br />
              1. Please find event type IDs from the &apos;Event types and ID Mapping&apos; listed below.
              <br />
              2. All event types will be included if the field is empty.
            </MDTypography>
          ),
          paramType: cellCaption("query"),
          dataType: badge("String"),
        },
        {
          num: cellText("4"),
          fieldName: cellText("Skip"),
          description: cellCaption("Number of events that should be skipped."),
          paramType: cellCaption("query"),
          dataType: badge("Int"),
        },
        {
          num: cellText("5"),
          fieldName: cellText("Take"),
          description: cellCaption("Number of events to take."),
          paramType: cellCaption("query"),
          dataType: badge("Int"),
        },
      ],
    },
    respObjKey: "vflux_get_events_response",
  },
  {
    mainHeadingStatic: "Column Filter Type and ID Mapping",
    mainGridIdStatic: "column-filter-mapping",
    mainHeadingDescStatic:
      "",
    staticTables: [
      {
        half: true,
        columns: [...idMappingColumnHeaders],
        rows: columnFilterRows,
      },
    ],
  },
  {
    mainHeadingStatic: "Event types and ID Mapping",
    mainGridIdStatic: "event-types-mapping",
    mainHeadingDescStatic:
      "",
    staticTables: [
      {
        half: true,
        columns: [...eventTypeColumnHeaders],
        rows: eventTypeRows,
      },
    ],
  },
  {
    mainHeadingStatic: "Response Object",
    mainGridIdStatic: "response-object",
    mainHeadingDescStatic: "",
  },
  {
    mainHeadingStatic: "Response Messages / Types",
    mainGridIdStatic: "response-messages",
    mainHeadingDescStatic:
      "",
    responseSplits,
  },
  // {
  //   mainHeadingStatic: "Disclaimer and Confidentiality Notice",
  //   mainGridIdStatic: "disclaimer",
  //   mainHeadingDescStatic:
  //     "the intended recipient and may not be used, published, or redistributed without prior written consent from Gato Systems.",
  //   mainHeadingDescStaticTwo:
  //     "The information is provided with care in these documents, Gato Systems makes no representations and gives no warranties of whatever nature in respect of these documents, including, but not limited to, the accuracy or completeness of any information, facts, and/or opinions contained therein. Gato Systems, its subsidiaries, the directors, employees, and agents cannot be held liable for the use of, and reliance on the information provided in these documents.",
  // },
];

export default vfluxApiData;
